/**
 * SOLID Review
 * S — Strong. One use case: the microphone or the camera came on — reported once per kind per room, never for a guest.
 * O — Strong. Whether to report is the Domain's presence plan.
 * L — Strong. Implements its use-case role exactly.
 * I — Strong. `execute`.
 * D — Strong. The decisions and media-server ports and the session's step, injected.
 */
import type { IContextRequest } from '../ports/IContextRequest';
import type { IPortClientDecisions } from '../../domain/ports/ClientDecisions.contract';
import type { IPropsClientInput } from '../../domain/ports/schemas/ClientChanges';
import type { ICommandMediaUsed } from '../../domain/ports/schemas/MediaMoves';
import type { IPortClientMediaServer } from '../ports/contracts';
import type { TPortOutcome } from '../ports/PortResult';
import type { IUseCase } from '../uml-components/IUseCase';
import { DONE, type IServiceSerialStep } from '../shared/ClientSteps';

export interface IConfigDepMediaUsed {
  readonly decisions: IPortClientDecisions;
  readonly media: IPortClientMediaServer;
  readonly step: IServiceSerialStep<IPropsClientInput>;
}

/** Presence (FE5c): the plan says whether this use is new for the room — kept as reported — and the report is best-effort. */
export class MediaUsedUseCase implements IUseCase<ICommandMediaUsed, TPortOutcome> {
  constructor(private readonly deps: IConfigDepMediaUsed) {}

  async execute(ctx: IContextRequest, command: ICommandMediaUsed): Promise<TPortOutcome> {
    const { decisions, step, media } = this.deps;
    const plan = await step.run(ctx, (input) => decisions.planPresence(ctx, input, command));
    if (!plan.success) return plan;
    if (plan.value.request === null) return DONE;
    return media.markPresence(ctx, plan.value.request);
  }
}
